import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { DIALOG_PATHS, MENU, NAV, RAIL, TABBAR, isCurrent, type NavKey } from '../nav.js';
import { useSignedIn } from '../session.js';
import { EventChip, EventPicker, MenuEvent } from './CurrentEvent.js';
import { Icon } from './Icon.js';
import { MyCardDialog } from './MyCardDialog.js';
import { SignOutButton } from './SignOut.js';
import { SyncStatus } from './SyncStatus.js';
import { useOffline } from '../offline/context.js';
import { initials } from './ui.js';

/**
 * Opens a screen. A window (Add contact) opens over the current page, which
 * stays behind it, as in the prototype; Back closes it.
 */
export function useOpen() {
  const navigate = useNavigate();
  const location = useLocation();
  return (path: string, options: { replace?: boolean } = {}) => {
    const background = (location.state as { background?: unknown } | null)?.background;
    if (DIALOG_PATHS.includes(path)) {
      navigate(path, { replace: options.replace, state: { background: background ?? location } });
    } else {
      navigate(path, { replace: options.replace });
    }
  };
}

/**
 * The signed-in frame, as docs/prototype.html: the rail on tablet landscape
 * and desktop (full height), the top bar (menu on phone, logo, current event
 * chip, the person), the side menu on phone and tablet portrait, and the
 * phone bar of quick actions. Entries follow the person's permissions.
 * The open side menu is a history step: Back closes it.
 */
export function Shell({ children }: { children: ReactNode }) {
  const { user, can, offline } = useSignedIn();
  const location = useLocation();
  const navigate = useNavigate();
  const open = useOpen();
  const menuOpen = (location.state as { menu?: boolean } | null)?.menu === true;
  // My business card (prototype: the name and avatar open it), a history step too.
  const cardOpen = (location.state as { card?: boolean } | null)?.card === true;
  const shown = (key: NavKey) => NAV[key].allowed(can);
  const name = `${user.firstName} ${user.lastName}`;
  // Under a window (Add contact), the page behind it stays the current one.
  const background = (location.state as { background?: { pathname: string } } | null)?.background;
  const pagePath = DIALOG_PATHS.includes(location.pathname)
    ? (background?.pathname ?? '/')
    : location.pathname;
  const current = (key: NavKey) => (isCurrent(NAV[key], pagePath) ? 'page' : undefined);

  const openMenu = () =>
    navigate(location.pathname + location.search, {
      state: { ...(location.state ?? {}), menu: true },
    });
  const closeMenu = () => navigate(-1);
  /** The card over the current page; from the side menu, in place of the menu step. */
  const openCard = () => {
    const state: Record<string, unknown> = { ...(location.state ?? {}), card: true };
    delete state.menu;
    navigate(location.pathname + location.search, { replace: menuOpen, state });
  };
  /** From the menu: the menu step is replaced by the screen. */
  const go = (path: string) => {
    if (menuOpen) open(path, { replace: true });
    else open(path);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, navigate]);

  return (
    <>
      <div className="app">
        <nav className="rail" aria-label="Sections">
          <button type="button" className="plate" aria-label="Home" onClick={() => go('/')}>
            <img src="/logo-rail.png" alt="" width={76} height={67} />
          </button>
          {RAIL.filter(shown).map((key) => (
            <button
              key={key}
              type="button"
              className={key === 'scan' ? 'scan' : undefined}
              aria-current={current(key)}
              onClick={() => go(NAV[key].path)}
            >
              {key === 'scan' ? (
                <span className="bubble">
                  <Icon name={NAV[key].icon} />
                </span>
              ) : (
                <Icon name={NAV[key].icon} />
              )}
              <span>{NAV[key].label}</span>
            </button>
          ))}
        </nav>
        <div className="col">
          <header className="topbar">
            <button
              type="button"
              className="menu-btn"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={openMenu}
            >
              <Icon name="menu" />
            </button>
            <button type="button" className="logo-btn" aria-label="Home" onClick={() => go('/')}>
              <img className="logo-r" src="/logo.png" alt="" width={112} height={22} />
            </button>
            {shown('events') && <EventChip />}
            <span className="spacer" />
            <SyncStatus />
            <button
              type="button"
              className="me-btn"
              aria-label={`My business card: ${name}`}
              onClick={openCard}
            >
              <span className="me-av">{initials(user.firstName, user.lastName)}</span>
              <span className="who">{name}</span>
            </button>
          </header>
          {offline && (
            <div className="offline-bar" role="status">
              <span className="odot" />
              You are offline. Your work is saved on this device and sent when the network is back.
            </div>
          )}
          <main id="main" tabIndex={-1}>
            {children}
          </main>
        </div>
      </div>

      <nav className="tabbar" aria-label="Quick actions">
        {TABBAR.filter(shown).map((key) => (
          <button
            key={key}
            type="button"
            className={key === 'scan' ? 'cap' : undefined}
            aria-current={current(key)}
            onClick={() => go(NAV[key].path)}
          >
            {key === 'scan' ? (
              <span className="bubble">
                <Icon name={NAV[key].icon} />
              </span>
            ) : (
              <Icon name={NAV[key].icon} />
            )}
            <span>{NAV[key].label}</span>
          </button>
        ))}
      </nav>

      {menuOpen && (
        <div className="drawer-root open">
          <div className="dr-bg" aria-hidden="true" onClick={closeMenu} />
          <nav className="drawer" aria-label="Main menu">
            <div className="dr-head">
              <img src="/logo.png" alt="Waste Robotics" width={120} height={23} />
              <button type="button" className="x" aria-label="Close menu" onClick={closeMenu}>
                ×
              </button>
            </div>
            <button
              type="button"
              className="dr-me"
              aria-label={`My business card: ${name}`}
              onClick={openCard}
            >
              <span className="me-av">{initials(user.firstName, user.lastName)}</span>
              <span>
                <b>{name}</b>
                <small>My business card, QR code</small>
              </span>
            </button>
            {shown('events') && <MenuEvent />}
            {MENU.map((group) => {
              const items = group.items.filter(shown);
              const extra = group.title === 'Me';
              if (items.length === 0 && !extra) return null;
              return (
                <div key={group.title} className="dr-g">
                  <span className="dr-gh">{group.title}</span>
                  {items.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className="dr-it"
                      aria-current={current(key)}
                      onClick={() => go(NAV[key].path)}
                    >
                      <Icon name={NAV[key].icon} />
                      <span>{NAV[key].menuLabel ?? NAV[key].label}</span>
                    </button>
                  ))}
                  {extra && <ChangesItem current={location.pathname === '/sync'} go={go} />}
                </div>
              );
            })}
            <div className="dr-foot">
              <SignOutButton />
            </div>
          </nav>
        </div>
      )}
      {shown('events') && <EventPicker />}
      {cardOpen && (
        <MyCardDialog
          onClose={() => navigate(-1)}
          onAccount={() => open('/account', { replace: true })}
        />
      )}
    </>
  );
}

/** Me > Changes not sent, with its count (portal addition: offline first). */
function ChangesItem({ current, go }: { current: boolean; go(path: string): void }) {
  return (
    <button
      type="button"
      className="dr-it"
      aria-current={current ? 'page' : undefined}
      onClick={() => go('/sync')}
    >
      <Icon name="sync" />
      <span>Changes not sent</span>
      <UnsentBadge />
    </button>
  );
}

function UnsentBadge() {
  const { counts } = useOffline();
  const unsent = counts.pending + counts.rejected;
  if (unsent === 0) return null;
  return <b className={`dr-b${counts.rejected > 0 ? ' red' : ''}`}>{unsent}</b>;
}
